import { Inject, Injectable } from '@angular/core';

import { LayerApi } from '../api/layer-api';
import { SearchApi } from '../api/search-api';
import { AssetIdColumnMap, AssetIdTypes } from '../keys';
import { IMCNotification, ISearchCriteria, LogLevel } from '../types';

import { AoiService } from './aoi.service';
import { DeepLinkProcessor } from './deep-link-processor.service';
import { GisToolsService } from './gis-tools.service';
import { IAuthenticationService } from './interfaces/IAuthenticationService';
import { MapService } from './map.service';
import { QueryStringParamService } from './query-string-param-service';

@Injectable({ providedIn: 'root' })
export class DeepLinkService {

    public parameters: { [key: string]: string | string[] } = {
        //showGrid: 'showGrid',
        //mpmsProjectId: ['mpms', 'mpmsNumber', 'proj', 'projectId'],
        //tmsSiteNumber: ['tmsSiteNumber', 'tmsSiteNo', 'tms'],
        //tmsPermanentSiteId: ['tmsPermanentSiteId'],
        //petlProjectId: ['petl', 'petl-project-id', 'petlProjectId'],
        //petlProjectDescription: ['petl-project-description'],
        layerId: ['layer-Id', 'layerId'],
        column: ['column', 'columnName'],
        value: ['value'],
        //program: 'program',
        //programStatus: 'programStatus',
        mapId: ['map-id', 'mapId'],
        counties: ['counties', 'countyNumbers'],
        countyNumber: ['ct', 'cn', 'cty', 'cty-code', 'county', 'countyNumber'],
        routeNumber: ['rt', 'route', 'st-rt-no', 'routeNumber'],
        segmentNumber: ['sg', 'seg', 'segment', 'segmentNumber'],
        latitude: ['lat', 'latitude'],
        longitude: ['long', 'longitude'],
        query: ['query'],
        bridgeKey: ['brkey', 'br-key', 'bridge-key'],
        bridgeId: ['bridge-id', 'bridgeid'],
        hopNumber: ['HOPNumber', 'hopNumber', 'hop'],
        hopApplicationId: ['HOPApplicationNumber', 'hopApplicationId'],
        districtNumber: ['district', 'dist', 'districtNumber'],
        projectId: ['projectId', 'projectIds'],
        brkey: ['bridgeKeys', 'brkeys', 'brKey'],
        hopPermitNumber: 'hopNumbers',
        hbmsPermitNumber: ['hbmsPermitNumber'],
        billboardId: ['billboardId'],
        junkyardId: ['junkyardId'],
        countyMunicipalityCode: ['countyMunicipalityCode'],
        municipalityId: ['muni', 'municipalityId'],
        municipalCode: ['municipalCode', 'municipalityCode'],
        corridorRt: ['corr_rt', 'corridorRouteNumber'],
        segmentNumberBegin: ['sg', 'segmentBegin', 'segmentNumberBegin', 'beginSegment', 'beginSegmentNumber', 'segment', 'segmentNumber'],
        segmentNumberEnd: ['sgend', 'segmentEnd', 'segmentNumberEnd', 'endSegment', 'endSegmentNumber'],
        mpoRpo: ['mporpo', 'planningPartnerShortname'],
        legislativeType: ['legtype', 'legislativeType'],
        legislativeDistrict: ['legdist', 'legislativeDistrict', 'legislativeDistrictNumber'],
        congressDistrictNumber: ['congressDistrictNumber'],
        houseDistrictNumber: ['houseDistrictNumber'],
        senateDistrictNumber: ['senateDistrictNumber'],
        address: ['address'],
        crossingId: ['crossing', 'crossingId', 'crossing-id', 'crossingIds', 'crossing-ids'],
        streetView: ['streetView', 'street-view', 'showStreetView', 'show-street-view', 'openStreetView', 'open-street-view']
    };

    isUserLoggedIn = false;
    userPermissions: string[];

    constructor(@Inject('authenticationService') public authService: IAuthenticationService,
        private readonly deepLinkProcessor: DeepLinkProcessor,
        private readonly mapService: MapService,
        private readonly AOIService: AoiService,
        private readonly gisToolsService: GisToolsService,
        private readonly queryStringParamService: QueryStringParamService,
        private readonly searchApi: SearchApi,
        private readonly layerApi: LayerApi
    ) {
        this.authService.permissions.subscribe(permissions => this.userPermissions = permissions);
        this.authService.user.subscribe(user => this.isUserLoggedIn = user != null);
    }

    public registerDeepLinkProcessors() {
        //commented out items are not used in onemap, but may need to be configured for other apps
        this.deepLinkProcessor.registerHandler(this.parameters, async (params: any) => {
            this.AOIService.setSearchCriterias([]);
            const currentSearchCriterias: any = this.AOIService.getSearchCriterias();
            const searchCriterias: ISearchCriteria[] = await this.getSearchCriterias(currentSearchCriterias, params);

            const assetIdSearchCriteria: any = this.getAssetIdSearchCriteria(params);
            //If we have an asset id, we need to make an API request to get additonal properties associated with the asset type (table name, column name, layer id, etc.)
            if (assetIdSearchCriteria) {
                const criteria = await this.formatAssetIdCriteria(assetIdSearchCriteria);
                searchCriterias.push(criteria);
            }

            //if we have some search criteria, perform the search
            if (searchCriterias.length) {
                await this.showBySearchCriterias(searchCriterias, params);

                if (params.streetView && (params.streetView === 'true' || params.streetView === 'yes' || params.streetView === '1')) {
                    this.AOIService.openStreetView(params);
                }
            } else if (params.mapId !== undefined && /^[0-9]{1,}$/.test(params.mapId)) {
                await this.openMap(params);
            }
        });
    }

    // Returns true if at least one of the deep link parameters is present in the query string.
    public deepLinkParamIsPresent(parameterKey: string = null) {
        let result = false;

        const queryStringParams = this.queryStringParamService.getQueryStringParams();
        const queryStringParamKeys = Object.keys(queryStringParams).map(p => p.toLowerCase());

        if (queryStringParamKeys.length > 0) {
            let parameterKeys = Object.keys(this.parameters);

            if (parameterKey) {
                parameterKeys = parameterKeys.filter(k => k === parameterKey);
            }

            result = parameterKeys.map(key => [this.parameters[key]].flat()).flat().some(key => queryStringParamKeys.includes(key.toLowerCase()));
        }

        return result;
    }

    public async getSearchCriterias(searchCriterias: any, params: any) {
        if (params.countyNumber) {
            searchCriterias.push(this.getRouteCriteria(params));
        } else if (params.corridorRt) {
            searchCriterias.push(this.getCorridorRouteCriteria(params));
        } else if (params.congressDistrictNumber) {
            searchCriterias.push(this.getCongressDistrictCriteria(params));
        } else if (params.houseDistrictNumber) {
            searchCriterias.push(this.getHouseDistrictCriteria(params));
        } else if (params.senateDistrictNumber) {
            searchCriterias.push(this.getSenateDistrictCriteria(params));
            //legislative types 'con', 'sen', 'hse' come from MPMS deeplinks.
        } else if (params.legislativeType === 'congress' || params.legislativeType === 'con') {
            searchCriterias.push({
                type: 'congressDistrict',
                congressDistrictNumber: parseInt(params.legislativeDistrict, 0)
            });
        } else if (params.legislativeType === 'house' || params.legislativeType === 'hse') {
            searchCriterias.push({
                type: 'houseDistrict',
                houseDistrictNumber: parseInt(params.legislativeDistrict, 0)
            });
        } else if (params.legislativeType === 'senate' || params.legislativeType === 'sen') {
            searchCriterias.push({
                type: 'senateDistrict',
                senateDistrictNumber: parseInt(params.legislativeDistrict, 0)
            });
        } else if (params.districtNumber) {
            searchCriterias.push(this.getDistrictCriteria(params));
        } else if (params.mpoRpo) {
            const criteria = await this.getPlanningPartnerCriteria(params);
            searchCriterias.push(criteria);
            //do county AND municipality combo together before
        } else if (params.countyNumber && params.municipalityId) {
            searchCriterias.push(this.getCountyMunicipalityCriteria(params));
        } else if (params.municipalityId) {
            const criteria = await this.getMunicipalityCriteria(params);
            searchCriterias.push(criteria);
        } else if (params.countyMunicipalityCode) {
            searchCriterias.push(this.getMunicipalityCodeCriteria(params));
        } else if (params.address) {
            searchCriterias.push({
                type: 'address',
                address: params.address
            });
        } else if (params.latitude && params.longitude) {
            searchCriterias.push(this.getLatLongCriteria(params));
        } else if (params.layerId) {
            searchCriterias.push(this.getLayerIdCriteria(params));
        }

        return searchCriterias;
    }

    private getCongressDistrictCriteria(params: any) {
        return {
            type: 'congressionalDistrict',
            congressionalDistrictNumber: parseInt(params.congressDistrictNumber, 0)
        };
    }

    private getHouseDistrictCriteria(params: any) {
        return {
            type: 'houseDistrict',
            houseDistrictNumber: parseInt(params.houseDistrictNumber, 0)
        };
    }

    private getSenateDistrictCriteria(params: any) {
        return {
            type: 'senateDistrict',
            senateDistrictNumber: parseInt(params.senateDistrictNumber, 0)
        };
    }

    private getCountyMunicipalityCriteria(params: any) {
        return {
            type: 'municipality',
            countyCode: params.countyNumber,
            municipalityCode: params.municipalityId
        };
    }

    private getRouteCriteria(params: any) {
        let searchCriteria: any;
        if (params.countyNumber && params.routeNumber) {
            searchCriteria = {
                type: 'route',
                countyCode: params.countyNumber,
                stateRouteNumber: params.routeNumber,
                isCorridor: params.corridorRt !== undefined
            };
            if (params.segmentNumberBegin) {
                searchCriteria.segBgn = params.segmentNumberBegin;
                if (params.segmentNumberEnd) {
                    searchCriteria.segEnd = params.segmentNumberEnd;
                }
            }
        } else if (params.countyNumber) {
            searchCriteria = {
                type: 'county',
                countyCode: params.countyNumber
            };
        }
        return searchCriteria;
    }

    private getCorridorRouteCriteria(params: any) {
        const searchCriteria: any = {
            type: 'route',
            stateRouteNumber: params.corridorRt,
            isCorridor: true
        };
        if (params.segmentNumberBegin) {
            searchCriteria.segBgn = params.segmentNumberBegin;
            if (params.segmentNumberEnd) {
                searchCriteria.segEnd = params.segmentNumberEnd;
            }
        }
        return searchCriteria;
    }

    private async getPlanningPartnerCriteria(params: any) {
        const planningPartners = await this.gisToolsService.getAllPlanningPartners('planningPartnerShortName');

        //find the planning partner with the same value as the mpoRpo, and use its exact casing.
        for (const partner of planningPartners) {
            if (partner.planningPartnerShortName.toLowerCase() === params.mpoRpo.toLowerCase()) {
                params.mpoRpo = partner.planningPartnerShortName;
            }
        }

        return {
            type: 'planningPartner',
            planningPartnerShortName: params.mpoRpo
        };
    }

    private async openMap(params: any) {
        const mapProps = { id: params.mapId, counties: undefined, showLoadErrorMessage: true, showTitleInErrorMessage: false };
        if (params.counties) {
            mapProps.counties = params.counties;
        }

        const mapId = parseInt(mapProps.id, 0);
        const mapProperties = 'id, extentJSON, name, uniqueName, description, data, isSystem, createdDate, lastEditedDate, isPublic, showOnAoi, basemap, layers{id, parentId, name}';
        let map: any;

        try {
            const maps = await this.mapService.getMaps([mapId], mapProperties);
            map = maps[0];
        }
        catch {
            console.error('Can\'t load map');
            this.mapService.clearLoading();
        }

        if (!map.isPublic && !map.isSystem) {
            try {
                await this.mapService.getMaps([mapId], 'userMaps { accessLevel }');
            } catch {
                this.mapService.clearLoading();

                const notification: IMCNotification = {
                    message: 'You are not authorized to view this map.',
                    level: LogLevel.Error,
                    isPrivate: false
                };

                if (!this.isUserLoggedIn) {
                    notification.message = 'This map requires an authorized user to view. Logging in may allow the map to be viewed.';
                }

                window.dispatchEvent(new CustomEvent('notAuthorized', { detail: notification, composed: true }));
                return;
            }
        }

        await this.mapService.openMap(map);
    }

    private getDistrictCriteria(params: any) {
        const districtNumber = parseInt(params.districtNumber, 0);
        const num = 10;
        const result = (districtNumber < num) ? '0' + districtNumber.toString() : districtNumber.toString();

        return {
            type: 'district',
            districtNumber: result
        };
    }

    private async getMunicipalityCriteria(params: any) {
        const municipalities = await this.gisToolsService.getAllMunicipalities('id,county,municipal_code');
        const muni = municipalities.find(m => m.id === params.municipalityId);
        return {
            type: 'municipality',
            countyCode: muni.county,
            municipalityCode: muni.municipalityId
        };
    }

    private getMunicipalityCodeCriteria(params: any) {
        const cntyLength = 2;
        const muniLength = 3;
        const countyCode = params.countyMunicipalityCode.substr(0, cntyLength);
        const muniCode = params.countyMunicipalityCode.substr(cntyLength, muniLength);
        return {
            type: 'municipality',
            countyCode,
            municipalityCode: muniCode
        };
    }

    private async formatAssetIdCriteria(assetIdSearchCriteria: ISearchCriteria) {
        assetIdSearchCriteria.columnName = AssetIdColumnMap[assetIdSearchCriteria.assetIdType];
        const options = await this.searchApi.getAoiAssetIdOptions(assetIdSearchCriteria.columnName, 'layerId, displayName');
        assetIdSearchCriteria.layerId = options.aoiAssetIdOptions[0].layerId;
        assetIdSearchCriteria.title = `${options.aoiAssetIdOptions[0].displayName}: ${assetIdSearchCriteria.data}`;
        return assetIdSearchCriteria;
    }

    private async showBySearchCriterias(searchCriterias: ISearchCriteria[], params: any) {
        if (!(await this.authorizeLayers(searchCriterias))) {
            return;
        }

        this.AOIService.setSearchCriterias(searchCriterias);
        if (params.mapId && params.mapId !== 'undefined') {
            params.mapId = <any>parseInt(params.mapId, 0);
            if (isNaN(<any>params.mapId)) {
                params.mapId = 'default';
            } else {
                await this.AOIService.setSelectedMapId(<any>params.mapId);
            }
        }

        await this.AOIService.showBySearchCriteriasAndMap();
    }

    private async authorizeLayers(searchCriterias: ISearchCriteria[]) {
        const layerIds = searchCriterias.filter(sc => sc.layerId != null).map(sc => sc.layerId);
        const result = await this.layerApi.getLayerPermissions(layerIds);
        const layerPermissions: Record<number, string[]> = {};
        let message;

        if (layerIds.length) {
            layerIds.forEach(layerId => layerPermissions[layerId] = []);
            result.forEach(lp => layerPermissions[lp.layerId].push(lp.name));

            for (const layerId of Object.keys(layerPermissions)) {
                const permissions = layerPermissions[layerId];

                if (permissions.includes('MapCore Public Layers')) {
                    continue;
                }

                if (!this.userPermissions.some(up => permissions.includes(up))) {
                    message = layerIds.length === 1
                        ? 'This layer requires an authorized user to view.'
                        : 'One or more layers require an authorized user to view.';

                    if (!this.isUserLoggedIn) {
                        message += ' Logging in may allow the layer to be viewed.';
                    }

                    break;
                }
            }
        }

        if (message) {
            const detail = { message, level: LogLevel.Error, isPrivate: false };
            window.dispatchEvent(new CustomEvent('notAuthorized', { detail, composed: true }));

            return false;
        } else {
            return true;
        }
    }

    private getAssetIdSearchCriteria(params: any) {
        let criteria: any = null;
        AssetIdTypes.forEach((item) => {
            if (params[item]) {
                if (!criteria) {
                    criteria = {};
                }
                criteria = {
                    type: 'assetId',
                    data: params[item],
                    ids: params[item].split(','),
                    assetIdType: item
                };
            }
        });
        return criteria;
    }

    private getLatLongCriteria(params: any) {
        return {
            type: 'latLong',
            latitude: params.latitude,
            longitude: params.longitude
        };
    }

    private getLayerIdCriteria(params: any): ISearchCriteria {
        const searchCriteria: ISearchCriteria = {
            type: 'assetId',
            layerId: parseInt(params.layerId, 10),
            displayName: params.column,
            columnName: params.column,
            data: params.value
        };
        if (params.query) {
            params.query = decodeURIComponent(params.query);
            searchCriteria.query = JSON.parse(params.query);
        }
        return searchCriteria;
    }
}
